import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Globe, Copy, Upload, X, FileText, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    service_type: '',
    message: ''
  });

  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      setTimeout(() => setCopiedText(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // File handling functions
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ];

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const maxFiles = 5;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    
    // Validate files
    const validFiles = newFiles.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        alert(`File type not allowed: ${file.name}`);
        return false;
      }
      if (file.size > maxFileSize) {
        alert(`File too large (max 10MB): ${file.name}`);
        return false;
      }
      return true;
    });

    // Check total file count
    if (files.length + validFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('first_name', formData.first_name);
      submitData.append('last_name', formData.last_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('service_type', formData.service_type);
      submitData.append('message', formData.message);
      
      // Append files if any
      files.forEach((file) => {
        submitData.append('files', file);
      });

      const response = await axios.post(`${API}/contact`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you for contacting us! We\'ll get back to you within 24 - 48 hours. A confirmation email has been sent to your inbox.'
        });
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          service_type: '',
          message: ''
        });
        setFiles([]);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        type: 'error',
        message: 'There was an error submitting your form. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      detail: '(954) 778-9764',
      link: 'tel:+19547789764'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      detail: 'info@professionalservicesnationwide.com',
      link: 'mailto:info@professionalservicesnationwide.com'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Website',
      detail: 'www.professionalservicesnationwide.com',
      link: 'https://www.professionalservicesnationwide.com'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Location',
      detail: 'Nationwide Coverage, USA',
      link: null
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Business Hours',
      detail: 'Mon-Fri: 9AM-6PM EST',
      link: null
    }
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="contact-hero-title">
            Get In Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto" data-testid="contact-hero-description">
            Discover how our solutions can help you achieve your goals. We're here to answer your questions and discuss your needs.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-gray-50" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 flex flex-col"
                data-testid={`contact-info-${index}`}
              >
                <div className="text-blue-800 mb-3">{info.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{info.title}</h3>
                {info.link ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-800 font-medium break-words">{info.detail}</p>
                    <div className="flex flex-col gap-2">
                      <a
                        href={info.link}
                        className="inline-flex items-center justify-center text-sm text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                        data-testid={`contact-link-${index}`}
                        {...(info.link.startsWith('http') && { 
                          target: '_blank', 
                          rel: 'noopener noreferrer' 
                        })}
                      >
                        {info.title === 'Phone' && 'Call Now'}
                        {info.title === 'Email' && 'Send Email'}
                        {info.title === 'Website' && 'Visit Website'}
                      </a>
                      {(info.title === 'Phone' || info.title === 'Email') && (
                        <button
                          onClick={() => handleCopy(info.detail, info.title)}
                          className="inline-flex items-center justify-center text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg font-semibold transition-colors"
                        >
                          <Copy size={16} className="mr-2" />
                          {copiedText === info.title ? '✓ Copied!' : 'Copy to Clipboard'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 break-words">{info.detail}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Appointment Scheduling Section */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-800" data-testid="appointment-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-2xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Calendar className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    Schedule an Appointment
                  </h2>
                </div>
                <p className="text-gray-600 max-w-lg">
                  Prefer to speak with us directly? Book a convenient time slot for a consultation with our team.
                </p>
              </div>
              <div className="flex-shrink-0">
                {/* Google Calendar Appointment Button */}
                <a
                  href="https://calendar.google.com/calendar/appointments/schedules/AcZssZ3knWXEBeADie1CU27c_eTKRdlRyveEryMtS7JSgLW8nCAcXmTuqq8Q9MSEIbW6enOtzZniij5m?gv=true&ctz=America/New_York"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 bg-blue-500 text-white font-semibold text-lg rounded-lg hover:bg-blue-600 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-transform"
                  data-testid="appointment-button"
                >
                  <Calendar className="w-6 h-6 mr-3" />
                  Book an Appointment
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="form-title">
                Send Us a Message
              </h2>
              <p className="text-gray-600" data-testid="form-description">
                Please be as detailed as possible so we can better understand your needs.
              </p>
            </div>

            {/* Status Messages */}
            {status.message && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center ${
                  status.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
                data-testid="form-status-message"
              >
                {status.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="first_name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="John"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="last_name"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Doe"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="john.doe@example.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="(555) 123-4567"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Service Type Dropdown */}
              <div className="mb-6">
                <label
                  htmlFor="service_type"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Service Type Needed *
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                  data-testid="input-service-type"
                >
                  <option value="">-- Select a Service --</option>
                  <option value="Bookkeeping & Accounting">Bookkeeping & Accounting</option>
                  <option value="Workflow Mapping & Analysis">Workflow Mapping & Analysis</option>
                  <option value="Business Automation">Business Automation</option>
                  <option value="Payroll Services">Payroll Services</option>
                  <option value="Notary Services">Notary Services</option>
                  <option value="Estate Planning">Estate Planning</option>
                  <option value="Insurance Solutions">Insurance Solutions</option>
                  <option value="Business Essentials">Business Essentials</option>
                  <option value="Website Building & Design">Website Building & Design</option>
                  <option value="AI Automation Services">AI Automation Services</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="message"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder="Please describe how we can help you..."
                  data-testid="input-message"
                ></textarea>
              </div>

              {/* File Upload Section */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Attach Files (Optional)
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  data-testid="file-drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInput}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-blue-600 hover:text-blue-700 font-semibold"
                  >
                    Click to upload files
                  </button>
                  <p className="text-gray-500 text-sm mt-1">or drag and drop</p>
                  <p className="text-gray-400 text-xs mt-2">
                    PDF, Word, Excel, Images (Max 10MB each, up to 5 files)
                  </p>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="file-list">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                        data-testid={`file-item-${index}`}
                      >
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 text-blue-600 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                          data-testid={`remove-file-${index}`}
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-800 text-white font-semibold py-4 px-6 rounded-lg hover:bg-blue-900 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="ml-2" size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-20 bg-blue-50" data-testid="response-time-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-testid="response-time-title">
            What Happens Next?
          </h2>
          <p className="text-gray-600 mb-6">
            Our team typically responds to all inquiries within 24 - 48 hours during business days.
            For urgent matters, please call us directly at (954) 778-9764 or email us at info@professionalservicesnationwide.com. 
            Visit our website at www.professionalservicesnationwide.com for more information.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white p-6 rounded-lg shadow-md" data-testid="step-1">
              <div className="text-3xl font-bold text-blue-800 mb-2">1</div>
              <h3 className="font-semibold text-gray-900 mb-2">We Review</h3>
              <p className="text-sm text-gray-600">Your message is reviewed by our team</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-testid="step-2">
              <div className="text-3xl font-bold text-blue-800 mb-2">2</div>
              <h3 className="font-semibold text-gray-900 mb-2">We Respond</h3>
              <p className="text-sm text-gray-600">We reach out within 24 - 48 hours</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-testid="step-3">
              <div className="text-3xl font-bold text-blue-800 mb-2">3</div>
              <h3 className="font-semibold text-gray-900 mb-2">We Deliver</h3>
              <p className="text-sm text-gray-600">We provide solutions tailored to you</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;